import React, { useCallback, useEffect, useReducer } from 'react';
import { css } from '@emotion/react';
import copy from 'copy-to-clipboard';
import { useApi } from '@core/api/hooks/useApi';
import { VideoEmailEmbedSize, VideoEmailEmbedType, } from '@core/api/types/api_video';
import { CloseX40x40 } from '@core/assets/CloseX40x40';
import { useContextTranslation } from '@core/i18n';
import { getBaseUrl } from '@core/sharing';
import { sendPixelTrackingWithSession } from '@core/tracking';
import { PlayerModal } from '@embed/components/VideoPlayer/Player3/components/PlayerModal';
import { useGetPlayerNestedTrackingPayload } from '@embed/components/VideoPlayer/Player3/hooks/useGetPlayerNestedTrackingPayload';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import Modal from '@player/components/Modal';
import { PlayerButton } from '@player/components/PlayerButton';
import { ErrorModal } from '@player/features/modal/components/ErrorModal';
import { EMAIL_EMBED_COPY_BUTTON } from '@player/testIds';
import { EMAIL_EMBED_CLICK } from '@player/tracking/endpoints';
import { CloseModalButton, ContentWrapper, CopyCodeButton, DividerLine, EmailEmbedModalWrapper, Heading, HeadingWrapper, Subheading, } from './EmailEmbedModal.styles';
import { ActionTypes, emailEmbedReducer } from './emailEmbedReducer';
import { EmailEmbedSizeSection } from './EmailEmbedSizeSection';
import { EmailEmbedSuccessModal } from './EmailEmbedSuccessModal';
import { EmailEmbedTypeSection } from './EmailEmbedTypeSection';
const INITIAL_STATE = {
    emailEmbedCodeToCopy: undefined,
    embedType: VideoEmailEmbedType.ANIMATED,
    embedSize: VideoEmailEmbedSize.BEST_FIT,
    isLoading: false,
    isShowingSuccessModal: false,
    isShowingErrorMessage: false,
};
/**
 *
 */
export const EmailEmbedModal = ({ onClose, videoId, }) => {
    const { t } = useContextTranslation();
    const params = useStore(({ params }) => params);
    const getPlayerNestedTrackingPayload = useGetPlayerNestedTrackingPayload();
    const baseUrl = getBaseUrl({ params });
    const { getVideoEmailEmbed } = useApi();
    const [{ emailEmbedCodeToCopy, embedType, embedSize, isLoading, isShowingSuccessModal, isShowingErrorMessage, }, dispatch,] = useReducer(emailEmbedReducer, INITIAL_STATE);
    useEffect(() => {
        async function fetchEmail() {
            try {
                dispatch({ type: ActionTypes.VIDEO_EMAIL_EMBED_LOAD_STARTED });
                const result = await getVideoEmailEmbed({
                    videoId,
                    embedSize,
                    embedType,
                    baseUrl,
                });
                if (!result) {
                    throw new Error();
                }
                dispatch({
                    type: ActionTypes.VIDEO_EMAIL_EMBED_LOAD_SUCCEEDED,
                    payload: result,
                });
            }
            catch (e) {
                dispatch({
                    type: ActionTypes.VIDEO_EMAIL_EMBED_LOAD_FAILED,
                });
            }
        }
        fetchEmail();
    }, [embedType, embedSize, baseUrl, getVideoEmailEmbed, videoId]);
    const onEmbedTypeChange = useCallback((e) => dispatch({
        type: ActionTypes.EMBED_TYPE_UPDATED,
        payload: e.target.value,
    }), [dispatch]);
    const onEmbedSizeChange = useCallback((e) => dispatch({
        type: ActionTypes.EMBED_SIZE_UPDATED,
        payload: e.target.value,
    }), [dispatch]);
    const trackEmailEmbedCopyClick = useCallback(({ videoId }) => {
        sendPixelTrackingWithSession({
            endpoint: EMAIL_EMBED_CLICK,
            data: {
                event_properties: {
                    ...getPlayerNestedTrackingPayload(),
                    video_id: videoId,
                    fwtrack_poster_size: embedSize,
                    fwtrack_poster_type: embedType,
                    _playlist_id: params?.playlist,
                },
            },
            host: params?.api_host_tracking,
        });
    }, 
    // TODO(ZE-231): Fix the deps and re-enable the rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const handleCopyCode = async () => {
        trackEmailEmbedCopyClick({ videoId });
        if (emailEmbedCodeToCopy) {
            try {
                const HtmlTextBlob = new Blob([emailEmbedCodeToCopy], {
                    type: 'text/html',
                });
                const PlainTextBlob = new Blob([emailEmbedCodeToCopy], {
                    type: 'text/plain',
                });
                await navigator.clipboard.write([
                    new ClipboardItem({
                        [HtmlTextBlob.type]: HtmlTextBlob,
                        [PlainTextBlob.type]: PlainTextBlob,
                    }),
                ]);
            }
            catch {
                /* GROW-236 - ClipboardItem is not supported by FF, FF Android
                and IE, so as a fallback we will copy just the text/html  */
                copy(emailEmbedCodeToCopy, {
                    format: 'text/plain',
                });
            }
            finally {
                dispatch({ type: ActionTypes.COPY_EMAIL });
            }
        }
        else {
            dispatch({
                type: ActionTypes.VIDEO_EMAIL_EMBED_LOAD_FAILED,
            });
        }
    };
    return isShowingErrorMessage ? (<ErrorModal title={t('player:Error')} description={t('player:These was an error, please try again.')} footer={<PlayerButton onClick={() => dispatch({
                type: ActionTypes.ERROR_DISMISSED,
            })}>
          {t('player:Try Again')}
        </PlayerButton>}/>) : (<PlayerModal onBackdropClick={onClose}>
      <Modal.Menu>
        {isShowingSuccessModal ? (<EmailEmbedSuccessModal onClose={onClose}/>) : (<EmailEmbedModalWrapper>
            <CloseModalButton onClick={onClose} aria-label={t('player:Close')}>
              <CloseX40x40 css={css `
                  width: 44px;
                  height: 44px;
                `} label={t('player:Close Email Embed Modal')}/>
            </CloseModalButton>
            <HeadingWrapper>
              <Heading marginBottom={13}>{t('player:Embed to Email')}</Heading>
              <Subheading>
                {t('player:Choose your preferred size for embedding.')}
              </Subheading>
            </HeadingWrapper>
            <ContentWrapper>
              <EmailEmbedTypeSection onEmbedTypeChange={onEmbedTypeChange}/>
              <DividerLine />
              <Heading marginBottom={25}>{t('player:Size')}</Heading>
              <EmailEmbedSizeSection onEmbedSizeChange={onEmbedSizeChange}/>
            </ContentWrapper>
            <CopyCodeButton data-testid={EMAIL_EMBED_COPY_BUTTON} disabled={isLoading} isLoading={isLoading} onClick={handleCopyCode}>
              {t('player:Copy Code')}
            </CopyCodeButton>
          </EmailEmbedModalWrapper>)}
      </Modal.Menu>
    </PlayerModal>);
};
